import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface Step7LoadingProps {
  onNext: () => void;
}

const Step7Loading: React.FC<Step7LoadingProps> = ({ onNext }) => {
  const [progress, setProgress] = useState(51);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    
    interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95 && !hasCompleted) {
          console.log('Step7Loading: Progress reached 95%, calling onNext in 500ms');
          setHasCompleted(true);
          clearInterval(interval);
          timeoutId = setTimeout(() => {
            console.log('Step7Loading: Calling onNext now');
            onNext();
          }, 500);
          return 95;
        }
        return Math.min(95, prev + Math.random() * 3 + 1);
      });
    }, 200);

    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [onNext]); // Removed hasCompleted from dependencies to prevent race condition

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      className="p-6 max-w-lg mx-auto text-center"
    >
      {/* Progress Circle */}
      <div className="mb-8 relative">
        <div className="w-32 h-32 mx-auto">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#E5E7EB"
              strokeWidth="8"
              fill="none"
            />
            <motion.circle
              cx="50"
              cy="50"
              r="45"
              stroke="url(#gradient)"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 45}`}
              strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
              animate={{
                strokeDashoffset: `${2 * Math.PI * 45 * (1 - progress / 100)}`
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2A2D8E" />
                <stop offset="100%" stopColor="#7E3AFF" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-700">{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      {/* Loading Text */}
      <motion.h2 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-xl font-semibold text-gray-800 mb-8"
      >
        Crafting your personalized roadmap for building with AI...
      </motion.h2>

      {/* Testimonial Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      >
        <h3 className="font-bold text-lg text-gray-900 mb-4">
          Join Thousands Who Are Building Smarter with LovaBuild
        </h3>
        
        <div className="text-left">
          <h4 className="font-semibold text-gray-800 mb-2">
            Game-Changing for My Startup!
          </h4>
          
          <p className="text-gray-600 mb-4 leading-relaxed">
            "I launched my first web app in weeks, not months, thanks to the AI strategies from LovaBuild. It felt like having an entire dev team at my fingertips!"
          </p>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">- Sarah L., Entrepreneur</span>
            <div className="flex text-yellow-400">
              {'★'.repeat(5)}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Step7Loading;